import { AdvancedSearchInfoLink } from 'components/Blotters/advanced-search-info-link';
import { ActivityFilter } from 'components/Blotters/Filter/ActivityFilter';
import { AdvancedFilterButton } from 'components/Blotters/Filter/AdvancedFilterButton';
import { BlotterTitleContainer, BlotterTitleLabel } from 'components/Blotters/Filter/blotter-title';
import { DateFilter } from 'components/Blotters/Filter/DateFilter';
import { DesksFilter } from 'components/Blotters/Filter/DesksFilter';
import { ExportCsv } from 'components/Blotters/Filter/ExportCsv';
import { FullTextSearch } from 'components/Blotters/Filter/full-text-search';
import { ConnectedQuotesAdvancedFilters } from 'components/Blotters/Quotes/Filter/AdvancedFilter.connect';
import { useBlotterItemsFetch } from 'components/Blotters/Quotes/Filter/hooks';
import { useAppSelector } from 'hooks/useAppSelector';
import { useToggleFeature } from 'hooks/useToggleFeature';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getQuoteBlotterAvailablePlatforms,
  getQuoteBlotterAvailablePlatformsFetchingStatus,
} from 'state/blotters/quote/quoteBlotter.selector';

import { BlotterErrorIcon } from '../../BlotterErrorIcon';
import { BlotterSpinner } from '../../BlotterSpinner';
import { PlatformFilterSelector } from '../../Filter/Platform/PlatformFilter';
import type { FilterDispatchProps, FilterOwnProps, FilterStateProps } from './Filter.connect';
import { connectFilterComponent } from './Filter.connect';
import { ProductFilterComponent } from './ProductFilter';

export type QuoteBlotterFilterProps = FilterOwnProps & FilterStateProps & FilterDispatchProps;

export const QuoteBlotterFilter: FC<QuoteBlotterFilterProps> = ({
  isInternal,
  error,
  loading,
  activityFilter,
  dateFilter,
  advancedFilters,
  quickSearchTerm,
  platformFilter,
  productFilter,
  deskFilter,
  lastBlotterRequest,
  onActivityFilterChanged,
  onDateFilterChanged,
  onProductFilterChanged,
  onPlatformFilterChanged,
  onDeskFilterChanged,
  onAdvancedFilterToggled,
  onFullTextSearchChanged,
}) => {
  const { formatMessage } = useIntl();
  const isSalesDeskFilterEnabled = useToggleFeature('salesDeskFilter');
  const fetch = useBlotterItemsFetch(lastBlotterRequest);
  const fetchingPlatformsStatus = useAppSelector(getQuoteBlotterAvailablePlatformsFetchingStatus);
  const platforms = useAppSelector(getQuoteBlotterAvailablePlatforms);

  return (
    <div className="quotes-blotter-filters w-100 align-items-center border-top" data-e2e="quotes-blotter-filters">
      <div className="w-100 d-flex flex-row flex-wrap flex-md-nowrap">
        <BlotterTitleContainer>
          <BlotterTitleLabel>
            <FormattedMessage id="quote.blotter.filter.title" />
          </BlotterTitleLabel>
          {error && <BlotterErrorIcon message={formatMessage({ id: 'blotter.filter.label.error' })} />}
          <BlotterSpinner className="flex-shrink-0 ms-2 my-auto" hide={!loading} />
        </BlotterTitleContainer>
        <ActivityFilter value={activityFilter} onSelect={onActivityFilterChanged} />
        {isSalesDeskFilterEnabled && activityFilter === 'myTeam' && (
          <DesksFilter deskFilter={deskFilter} onDeskFilterChanged={onDeskFilterChanged} />
        )}
        <ProductFilterComponent value={productFilter} onSelect={onProductFilterChanged} />
        {isInternal && (
          <PlatformFilterSelector
            className="width-fit-content ms-2"
            platformFilter={platformFilter}
            onPlatformFilterChanged={onPlatformFilterChanged}
            fetchingPlatformsStatus={fetchingPlatformsStatus}
            platforms={platforms}
          />
        )}
        <DateFilter value={dateFilter} onChange={onDateFilterChanged} />
        <FullTextSearch value={quickSearchTerm} onChange={onFullTextSearchChanged} debounce>
          <AdvancedSearchInfoLink className="btn-flat-secondary btn-xl border-start border-opacity-40 d-inline-block" />
          <ExportCsv className="d-inline-block" fetch={fetch} disabled={error || lastBlotterRequest === undefined} />
          <AdvancedFilterButton value={advancedFilters} onToggle={onAdvancedFilterToggled} />
        </FullTextSearch>
      </div>

      {advancedFilters && <ConnectedQuotesAdvancedFilters />}
    </div>
  );
};

export const ConnectedQuoteBlotterFilter = connectFilterComponent(QuoteBlotterFilter);
